/**
 * Admin permission registry.
 *
 * Every gate in the UI is expressed as a permission KEY (`section.members`,
 * `reports.finance`, `memberships.manage`, …). A role (access_level) is just
 * the default set of keys an admin holds; the registry below is the single
 * place that mapping lives. Sidebar sections, route guards, Reports tabs and
 * action buttons all resolve through `usePermissions().can(key)`.
 *
 * This is the CLIENT boundary. Anything that must hold against a direct
 * PostgREST or edge-function call is enforced server-side (RLS, edge function
 * tier checks). Keep the two in mind as separate layers; never cite this file
 * as proof that data is protected.
 */

export const ACCESS_LEVELS = {
  STAFF: 'staff',
  MANAGER: 'manager',
  SUPER_ADMIN: 'super_admin'
} as const;

export type AccessLevel = typeof ACCESS_LEVELS[keyof typeof ACCESS_LEVELS];

/** Ladder order, lowest first. Used for `isAtLeast` and for column order in the matrix. */
export const ACCESS_LEVEL_ORDER: readonly AccessLevel[] = ['staff', 'manager', 'super_admin'];

export const PERMISSION_GROUPS = {
  operations: 'Operations',
  actions: 'Actions',
  administration: 'Administration',
  configuration: 'Configuration',
  system: 'System',
  reports: 'Reports',
} as const;

export type PermissionGroup = keyof typeof PERMISSION_GROUPS;

export interface PermissionDefinition {
  /** Human label shown in the permission matrix and denial messages. */
  label: string;
  group: PermissionGroup;
  /** Roles that hold this permission by default. */
  roles: readonly AccessLevel[];
  /** Optional one-liner for the matrix page. */
  description?: string;
}

const ALL: readonly AccessLevel[] = ['staff', 'manager', 'super_admin'];
const MANAGER_UP: readonly AccessLevel[] = ['manager', 'super_admin'];
const SUPER_ONLY: readonly AccessLevel[] = ['super_admin'];

/**
 * Least privilege by default: a staff or manager admin starts with only the
 * three sections nobody can work without, and every other section is given to
 * them individually as a grant (the "Extra permissions" card on their page).
 *
 * `BY_GRANT` is spelled separately from `SUPER_ONLY` even though the arrays
 * match, because they mean different things: `SUPER_ONLY` is "this is a
 * super-admin concern", `BY_GRANT` is "scoped per person; super admins hold it
 * so the club is never locked out of its own tools". A new section added with
 * `BY_GRANT` is off for everyone below super_admin until granted, which is the
 * point — nothing is silently handed to 14 people.
 */
const BY_GRANT: readonly AccessLevel[] = ['super_admin'];

export const PERMISSIONS = {
  // --- Operations ------------------------------------------------------------
  // The baseline every admin needs to do anything at all.
  'section.dashboard': { label: 'Dashboard', group: 'operations', roles: ALL },
  'section.members': { label: 'Members', group: 'operations', roles: ALL },
  'section.check_ins': { label: 'Check-ins', group: 'operations', roles: ALL },

  // Granted per person to staff and managers. See BY_GRANT above.
  'section.classes': { label: 'Classes', group: 'operations', roles: BY_GRANT },
  'section.memberships': { label: 'Memberships', group: 'operations', roles: BY_GRANT },
  'section.personal_training': { label: 'Personal Training', group: 'operations', roles: BY_GRANT },
  'section.pt_sessions': { label: 'PT Sessions', group: 'operations', roles: BY_GRANT },
  'section.pt_packs': { label: 'PT Packs', group: 'operations', roles: BY_GRANT },
  'section.gift_cards': { label: 'Gift Cards', group: 'operations', roles: BY_GRANT },
  'section.spaces': { label: 'Spaces', group: 'operations', roles: BY_GRANT },
  'section.lockers': { label: 'Lockers', group: 'operations', roles: BY_GRANT },
  'section.birthdays': { label: 'Birthdays', group: 'operations', roles: BY_GRANT },
  'section.agent': { label: 'Agent', group: 'operations', roles: BY_GRANT },
  'section.news': { label: 'News', group: 'operations', roles: BY_GRANT },
  'section.feedback': { label: 'Feedback', group: 'operations', roles: BY_GRANT },
  'section.bpa': { label: 'BPA', group: 'operations', roles: BY_GRANT },
  // Sends an unrecallable message to every phone in a segment, so it is
  // scoped per person like the other grant-only sections rather than handed
  // to a tier. The send-push-campaign edge function re-checks this same key
  // server-side (role default OR grant row) — the one section where the
  // client gate alone was not enough.
  'section.push_notifications': { label: 'Push notifications', group: 'operations', roles: BY_GRANT },

  // --- Actions: things a section may show but only some roles may do -------
  'memberships.manage': {
    label: 'Manage memberships',
    group: 'actions',
    roles: MANAGER_UP,
    description: 'Cancel, delete, transfer, upgrade or refund a membership.',
  },
  'gift_cards.manage': {
    label: 'Manage gift cards',
    group: 'actions',
    roles: MANAGER_UP,
    description: 'Cancel or extend a gift card.',
  },

  // --- Administration -------------------------------------------------------
  'section.admin_users': { label: 'Admin Users', group: 'administration', roles: SUPER_ONLY },
  'section.activity_logs': { label: 'Activity Logs', group: 'administration', roles: SUPER_ONLY },
  'section.reports': { label: 'Reports', group: 'administration', roles: SUPER_ONLY },
  'section.employees': { label: 'Employees', group: 'administration', roles: SUPER_ONLY },
  'section.inventory': { label: 'Inventory', group: 'administration', roles: SUPER_ONLY },

  // --- Configuration: catalogue / type definitions --------------------------
  'section.membership_types': { label: 'Membership Types', group: 'configuration', roles: SUPER_ONLY },
  'section.class_categories': { label: 'Class Categories', group: 'configuration', roles: SUPER_ONLY },
  'section.class_locations': { label: 'Class Locations', group: 'configuration', roles: SUPER_ONLY },
  'section.coaches': { label: 'Coaches', group: 'configuration', roles: SUPER_ONLY },
  'section.pt_pack_types': { label: 'PT Pack Types', group: 'configuration', roles: SUPER_ONLY },

  // --- System ---------------------------------------------------------------
  'section.style_book': { label: 'Style Book', group: 'system', roles: SUPER_ONLY },
  'section.odoo': { label: 'Odoo', group: 'system', roles: SUPER_ONLY },

  // --- Reports: one key per tab. Holding any tab key also opens the Reports
  //     section itself (see DERIVED_PERMISSIONS), so a per-user grant of a
  //     single tab is all it takes to open Reports on just that tab.
  'reports.members': { label: 'Members', group: 'reports', roles: SUPER_ONLY },
  'reports.member-types': { label: 'Member Types', group: 'reports', roles: SUPER_ONLY },
  'reports.member-creation': { label: 'Member Creation', group: 'reports', roles: SUPER_ONLY },
  'reports.nationality': { label: 'Nationality', group: 'reports', roles: SUPER_ONLY },
  'reports.age': { label: 'Age', group: 'reports', roles: SUPER_ONLY },
  'reports.gender': { label: 'Gender', group: 'reports', roles: SUPER_ONLY },
  'reports.memberships': { label: 'Memberships', group: 'reports', roles: SUPER_ONLY },
  'reports.renewals': { label: 'Renewals', group: 'reports', roles: SUPER_ONLY },
  'reports.freezes': { label: 'Freezes', group: 'reports', roles: SUPER_ONLY },
  'reports.finance': { label: 'Finance', group: 'reports', roles: SUPER_ONLY },
  'reports.refunds': { label: 'Refunds', group: 'reports', roles: SUPER_ONLY },
  'reports.fnb': { label: 'F&B', group: 'reports', roles: SUPER_ONLY },
  'reports.merch': { label: 'Merch', group: 'reports', roles: SUPER_ONLY },
  'reports.classes': { label: 'Classes', group: 'reports', roles: SUPER_ONLY },
  'reports.pt-sessions': { label: 'PT Sessions', group: 'reports', roles: SUPER_ONLY },
  'reports.check-ins': { label: 'Check-Ins', group: 'reports', roles: SUPER_ONLY },
  'reports.call-booths': { label: 'Call Booth', group: 'reports', roles: SUPER_ONLY },
  'reports.lockers': { label: 'Lockers', group: 'reports', roles: SUPER_ONLY },
  'reports.friend-passes': { label: 'Friend Passes', group: 'reports', roles: SUPER_ONLY },
  'reports.mobile-app': { label: 'Mobile app', group: 'reports', roles: SUPER_ONLY },
  'reports.push-notifications': { label: 'Push', group: 'reports', roles: SUPER_ONLY },
  'reports.wallet-passes': { label: 'Wallet Passes', group: 'reports', roles: SUPER_ONLY },
  'reports.emails': { label: 'Emails', group: 'reports', roles: SUPER_ONLY },
  'reports.deleted-members': { label: 'Deleted Members', group: 'reports', roles: SUPER_ONLY },
} as const satisfies Record<string, PermissionDefinition>;

export type PermissionKey = keyof typeof PERMISSIONS;

export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as PermissionKey[];

/** Reports tab id → its permission key. Tab ids are the `?tab=` values. */
export type ReportTabId = Extract<PermissionKey, `reports.${string}`> extends `reports.${infer Id}` ? Id : never;

export const reportTabPermission = (tabId: ReportTabId): PermissionKey => `reports.${tabId}`;

/** True when `level` holds `key` by default. Unknown keys are simply not held. */
export function roleHasPermission(level: AccessLevel, key: PermissionKey): boolean {
  const def = (PERMISSIONS as Record<string, PermissionDefinition | undefined>)[key];
  return def?.roles.includes(level) ?? false;
}

/** Every key `level` holds by default, in registry order. */
export function getRolePermissions(level: AccessLevel): PermissionKey[] {
  return PERMISSION_KEYS.filter((key) => roleHasPermission(level, key));
}

/**
 * Keys that are never granted on their own: an admin holds one whenever they
 * hold any key its rule matches. `section.reports` follows from holding at
 * least one Reports tab, so there is no separate "Reports" toggle to forget
 * (a tab grant without it used to leave the whole section locked).
 */
const DERIVED_PERMISSIONS: Partial<Record<PermissionKey, (held: PermissionKey) => boolean>> = {
  'section.reports': (held) => held.startsWith('reports.'),
};

/** True for keys that follow from other keys and so are not granted directly. */
export function isDerivedPermission(key: PermissionKey): boolean {
  return key in DERIVED_PERMISSIONS;
}

/** Every key an admin holds: role defaults, per-user grants, and what those imply. */
export function resolveEffectivePermissions(
  level: AccessLevel,
  grants: readonly PermissionKey[],
): Set<PermissionKey> {
  const held = new Set<PermissionKey>([...getRolePermissions(level), ...grants]);
  for (const [derived, impliedBy] of Object.entries(DERIVED_PERMISSIONS)) {
    if ([...held].some(impliedBy)) held.add(derived as PermissionKey);
  }
  return held;
}

/**
 * Operations sections a staff admin does NOT hold by default, i.e. the ones
 * scoped per person through a grant. Derived, so it cannot drift from the
 * registry; the backfill migration that preserved existing admins' access
 * (20260918140000) was generated from this same list.
 */
export function getGrantOnlySectionKeys(): PermissionKey[] {
  return PERMISSION_KEYS.filter(
    (key) => PERMISSIONS[key].group === 'operations' && !roleHasPermission('staff', key),
  );
}

/** Registry keys grouped for display, preserving registry order within a group. */
export function getPermissionsByGroup(): Record<PermissionGroup, PermissionKey[]> {
  const grouped = Object.fromEntries(
    (Object.keys(PERMISSION_GROUPS) as PermissionGroup[]).map((g) => [g, [] as PermissionKey[]]),
  ) as Record<PermissionGroup, PermissionKey[]>;
  for (const key of PERMISSION_KEYS) {
    grouped[PERMISSIONS[key].group].push(key);
  }
  return grouped;
}

// ---------------------------------------------------------------------------
// Sections: the navigation-level view of the registry. `SectionKey` names are
// what Layout.tsx, RequireSection and the older hasAccessTo(section) call
// sites use; each resolves to exactly one `section.*` permission key.
// ---------------------------------------------------------------------------

export const SECTION_TO_PERMISSION = {
  DASHBOARD: 'section.dashboard',
  MEMBERS: 'section.members',
  MEMBERSHIPS: 'section.memberships',
  GIFT_CARDS: 'section.gift_cards',
  PT_SESSIONS: 'section.pt_sessions',
  PT_PACKS: 'section.pt_packs',
  PERSONAL_TRAINING: 'section.personal_training',
  CLASSES: 'section.classes',
  SPACES: 'section.spaces',
  LOCKERS: 'section.lockers',
  CHECK_INS: 'section.check_ins',
  BIRTHDAYS: 'section.birthdays',
  AGENT: 'section.agent',
  NEWS: 'section.news',
  FEEDBACK: 'section.feedback',
  ADMIN_USERS: 'section.admin_users',
  MEMBERSHIP_TYPES: 'section.membership_types',
  CLASS_CATEGORIES: 'section.class_categories',
  CLASS_LOCATIONS: 'section.class_locations',
  COACHES: 'section.coaches',
  EMPLOYEES: 'section.employees',
  INVENTORY: 'section.inventory',
  PT_PACK_TYPES: 'section.pt_pack_types',
  ACTIVITY_LOGS: 'section.activity_logs',
  REPORTS: 'section.reports',
  STYLE_BOOK: 'section.style_book',
  ODOO: 'section.odoo',
  BPA: 'section.bpa',
  PUSH_NOTIFICATIONS: 'section.push_notifications',
} as const satisfies Record<string, PermissionKey>;

export type SectionKey = keyof typeof SECTION_TO_PERMISSION;

export const SECTION_KEYS = Object.keys(SECTION_TO_PERMISSION) as SectionKey[];

/**
 * Section view of the registry, derived — same shape the pre-registry matrix
 * had (`{ name, allowedRoles }`) so existing readers keep working.
 */
export const SECTION_PERMISSIONS: Record<SectionKey, { name: string; allowedRoles: readonly AccessLevel[] }> =
  Object.fromEntries(
    SECTION_KEYS.map((section) => {
      const def = PERMISSIONS[SECTION_TO_PERMISSION[section]];
      return [section, { name: def.label, allowedRoles: def.roles }];
    }),
  ) as Record<SectionKey, { name: string; allowedRoles: readonly AccessLevel[] }>;

/** Helper function to check if a role can see a section. */
export function hasAccess(userAccessLevel: AccessLevel, section: SectionKey): boolean {
  return roleHasPermission(userAccessLevel, SECTION_TO_PERMISSION[section]);
}

/** Helper function to get all accessible sections for a role. */
export function getAccessibleSections(userAccessLevel: AccessLevel): SectionKey[] {
  return SECTION_KEYS.filter((section) => hasAccess(userAccessLevel, section));
}

// Access level descriptions for the Admin Users page. The section lists are
// derived from the registry so this can't drift from what the app enforces.
export const ACCESS_LEVEL_DESCRIPTIONS: Record<AccessLevel, { name: string; description: string }> = {
  [ACCESS_LEVELS.STAFF]: {
    name: 'Staff',
    description:
      'Starts with Dashboard, Members and Check-ins. Every other section is given to them individually on their own page.',
  },
  [ACCESS_LEVELS.MANAGER]: {
    name: 'Manager',
    description:
      'Same starting sections as staff, plus membership and gift card management. Other sections are given individually.',
  },
  [ACCESS_LEVELS.SUPER_ADMIN]: {
    name: 'Super Admin',
    description: 'Full access to every section, including administration, configuration, system and reports.',
  },
};

export function getAccessLevelName(level: AccessLevel): string {
  return ACCESS_LEVEL_DESCRIPTIONS[level].name;
}
